import { Suspense, useEffect, useState, useRef, useCallback } from 'react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import './App.css';
import Header from './components/core/header/Header';
import keycloakconfig from './utils/AuthConfig/KeycloakConfig';
import 'bootstrap/dist/css/bootstrap.min.css';
import { checkAppAccessibility } from './utils/accessibility-check';
import PermissionError from './components/error/permission-error/PermissionError';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { TranslateEn } from './assets/i18n/translateEn';
import { TranslateFr } from './assets/i18n/translateFr';
import { ToastContainer } from 'react-toastify';
import AppRoutes from './utils/routes';
import { postLoginLogout } from './services/UserAuthorization';
import { FeatureProvider, useFeatures } from './utils/featureContext';
import { KEY_CLOAK_LOGOUT_URL } from './utils/constants';

i18n.use(initReactI18next).init({
    resources: {
        en: { translation: TranslateEn },
        fr: { translation: TranslateFr }
    },
    lng: "en",
    fallbackLng: "en",
    interpolation: { escapeValue: false }
});


const onload = {
    onLoad: 'login-required',
    checkLoginIframe: false,
    pkceMethod: 'S256',
};

const logoutOptions = {
    redirectUri: KEY_CLOAK_LOGOUT_URL,
};


const LOGIN_AUDIT_KEY = "LOGIN_AUDIT_DONE";

const getUserNameFromEmail = (email) => {
    if (!email || typeof email !== "string") return "";
    return email.split("@")[0];
};

const FeatureLoader = ({ userName, children }) => {
    const { loadFeatures } = useFeatures();

    useEffect(() => {
        if (userName) {
            loadFeatures(userName);
        }
    }, [userName, loadFeatures]);

    return children;
};

function KApp() {
    const [authCompleted, setAuthCompleted] = useState(false);
    const [user, setUser] = useState(null);
    const [userName, setUserName] = useState("");

    const userLoadedRef = useRef(false);

    // Check if user has permission to access the page
    const canUserAccess = () => {
        return user && checkAppAccessibility(user.UserType, user.FeatureList);
    }

    const signoutUser = useCallback(async () => {
        try {
            await postLoginLogout({
                action: "logout",
                userName
            });
        } catch (err) {
            console.error("Logout audit failed", err);
        } finally {
            sessionStorage.removeItem(LOGIN_AUDIT_KEY);
            keycloakconfig.logout(logoutOptions);
        }
    }, [userName]);

    const handleTokens = useCallback(({ token }) => {
        localStorage.setItem('keycloakToken', token);
         
        if (userLoadedRef.current) return;

        userLoadedRef.current = true;

        keycloakconfig.loadUserInfo().then((userInfo) => {
            console.log('Keycloak User Info:', userInfo);
            setUser(userInfo);
            const extractedUserName = getUserNameFromEmail(userInfo.email);
            setUserName(extractedUserName);
            localStorage.setItem('keycloakUsername', extractedUserName);

            const alreadyLogged = sessionStorage.getItem(LOGIN_AUDIT_KEY);
            if (!alreadyLogged) {
                postLoginLogout({
                    action: "login",
                    userName: extractedUserName,
                }).then(() => {
                    sessionStorage.setItem(LOGIN_AUDIT_KEY, "true");
                }).catch((err) => {
                    console.error("Login audit failed", err);
                });
            }
            setAuthCompleted(true);
        }).catch((err) => {
            console.error('Failed to load keycloak user info:', err);
        });
    }, []);

    return (
        <ReactKeycloakProvider
            authClient={keycloakconfig}
            initOptions={onload}
            onTokens={handleTokens}
            onTokenExpired={() => keycloakconfig.updateToken(3600)}
        >
            {
                authCompleted ? (
                    user && canUserAccess() ? (
                        <FeatureProvider>
                            <FeatureLoader userName={userName}>
                                <Suspense fallback="loading...">
                                    <div className="app-shell">
                                        <Header signout={signoutUser} user={user} />

                                        {/* The part that should fill the rest of the screen */}
                                        <main className="app-main">
                                            <AppRoutes />
                                        </main>

                                        <ToastContainer
                                            autoClose={3000}
                                            hideProgressBar
                                            newestOnTop={false}
                                            closeOnClick
                                            pauseOnFocusLoss
                                            draggable
                                            pauseOnHover
                                            style={{ top: '100px' }}
                                        />
                                    </div>
                                </Suspense>
                            </FeatureLoader>
                        </FeatureProvider>
                    ) : (
                        <PermissionError />
                    )
                ) : null}
        </ReactKeycloakProvider>
    );
}

export default KApp;